import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useAdmin } from '../../contexts/AdminContext';

function MemberDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { approveMember, rejectMember } = useAdmin();

  // For now, find the member from our mock data
  const member = mockMembers.find(m => m.id === parseInt(id));

  if (!member) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <h2 className="text-2xl font-bold text-hdlcc-textDark uppercase mb-4">
            Member Not Found
          </h2>
          <p className="mb-4 text-hdlcc-textDark">
            The member you&apos;re looking for doesn&apos;t exist.
          </p>
          <button
            onClick={() => navigate('/admin/members')}
            className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
          >
            Back to Members List
          </button>
        </div>
      </div>
    );
  }

  const isPending = member.status === 'pending';
  const isExpiringSoon =
    member.status === 'active' &&
    member.renewalDate &&
    (new Date(member.renewalDate) - new Date()) / (1000 * 60 * 60 * 24) <= 30;

  const handleApprove = async () => {
    const success = await approveMember(member.id);
    if (success) {
      navigate('/admin/members');
    }
  };

  const handleReject = async () => {
    const success = await rejectMember(member.id);
    if (success) {
      navigate('/admin/members');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-hdlcc-textDark uppercase flex items-center gap-3">
            {member.firstName} {member.lastName}
            <span
              className={`px-2 py-1 rounded text-sm ${
                member.status === 'active'
                  ? 'bg-green-900 text-green-200 border border-green-500'
                  : member.status === 'expired'
                    ? 'bg-red-900 text-red-200 border border-red-500'
                    : 'bg-yellow-900 text-yellow-200 border border-yellow-500'
              }`}
            >
              {member.status}
            </span>
          </h2>
          <p className="text-hdlcc-textMuted">Membership #{member.membershipNumber}</p>
        </div>
        <div className="flex gap-2">
          {isPending ? (
            <>
              <button
                onClick={handleApprove}
                className="bg-hdlcc-accentGreen text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
              >
                Approve
              </button>
              <button
                onClick={handleReject}
                className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 border border-gray-200 transition-colors"
              >
                Reject
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate(`/admin/members/${id}/edit`)}
              className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
            >
              Edit Member
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-hdlcc-bgWhite shadow-card border border-gray-200 p-4 rounded">
          <h3 className="text-lg font-bold text-hdlcc-textDark uppercase mb-4">
            Personal Information
          </h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-hdlcc-textMuted">Email</dt>
              <dd className="text-hdlcc-textDark">{member.email}</dd>
            </div>
            <div>
              <dt className="text-hdlcc-textMuted">Membership Type</dt>
              <dd className="capitalize text-hdlcc-textDark">{member.membershipType}</dd>
            </div>
            <div>
              <dt className="text-hdlcc-textMuted">Join Date</dt>
              <dd className="text-hdlcc-textDark">
                {new Date(member.joinDate).toLocaleDateString()}
              </dd>
            </div>
            {member.renewalDate && (
              <div>
                <dt className="text-hdlcc-textMuted">Renewal Date</dt>
                <dd className={isExpiringSoon ? 'text-yellow-600' : 'text-hdlcc-textDark'}>
                  {new Date(member.renewalDate).toLocaleDateString()}
                  {isExpiringSoon && ' (Expiring Soon)'}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="bg-hdlcc-bgWhite shadow-card border border-gray-200 p-4 rounded">
          <h3 className="text-lg font-bold text-hdlcc-textDark uppercase mb-4">
            Membership History
          </h3>
          <div className="space-y-4">
            <div className="border-l-2 border-hdlcc-accentRed pl-4">
              <p className="text-sm text-hdlcc-textMuted">Today</p>
              <p className="text-hdlcc-textDark">Viewed member profile</p>
            </div>
            {isPending && (
              <div className="border-l-2 border-yellow-500 pl-4">
                <p className="text-sm text-hdlcc-textMuted">
                  {new Date(member.joinDate).toLocaleString()}
                </p>
                <p className="text-hdlcc-textDark">Membership application submitted</p>
              </div>
            )}
            {!isPending && (
              <>
                <div className="border-l-2 border-hdlcc-accentGreen pl-4">
                  <p className="text-sm text-hdlcc-textMuted">
                    {new Date(member.joinDate).toLocaleString()}
                  </p>
                  <p className="text-hdlcc-textDark">Membership approved</p>
                </div>
                <div className="border-l-2 border-blue-500 pl-4">
                  <p className="text-sm text-hdlcc-textMuted">
                    {new Date(member.joinDate).toLocaleString()}
                  </p>
                  <p className="text-hdlcc-textDark">Membership activated</p>
                </div>
              </>
            )}
          </div>
        </div>

        {!isPending && (
          <>
            <div className="bg-hdlcc-bgWhite shadow-card border border-gray-200 p-4 rounded">
              <h3 className="text-lg font-bold text-hdlcc-textDark uppercase mb-4">
                Payment Information
              </h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-hdlcc-textMuted">Payment Method</dt>
                  <dd className="text-hdlcc-textDark">Direct Debit</dd>
                </div>
                <div>
                  <dt className="text-hdlcc-textMuted">Last Payment</dt>
                  <dd className="text-hdlcc-textDark">
                    £50.00 - {new Date().toLocaleDateString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-hdlcc-textMuted">Next Payment</dt>
                  <dd className="text-hdlcc-textDark">
                    £50.00 - {new Date(member.renewalDate).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="bg-hdlcc-bgWhite shadow-card border border-gray-200 p-4 rounded">
              <h3 className="text-lg font-bold text-hdlcc-textDark uppercase mb-4">
                Recent Activity
              </h3>
              <div className="space-y-4">
                <div className="border-l-2 border-hdlcc-accentRed pl-4">
                  <p className="text-sm text-hdlcc-textMuted">2 days ago</p>
                  <p className="text-hdlcc-textDark">Attended club meeting</p>
                </div>
                <div className="border-l-2 border-hdlcc-accentRed pl-4">
                  <p className="text-sm text-hdlcc-textMuted">1 week ago</p>
                  <p className="text-hdlcc-textDark">Updated contact information</p>
                </div>
                <div className="border-l-2 border-hdlcc-accentRed pl-4">
                  <p className="text-sm text-hdlcc-textMuted">2 weeks ago</p>
                  <p className="text-hdlcc-textDark">Registered for upcoming event</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Mock data for development
const mockMembers = [
  // Active Members
  {
    id: 101,
    membershipNumber: 'HDL001',
    firstName: 'Robert',
    lastName: 'Wilson',
    email: 'robert.wilson@example.com',
    membershipType: 'individual',
    status: 'active',
    joinDate: '2023-01-15',
    renewalDate: '2024-01-15',
  },
  // Pending Members (from AdminContext)
  {
    id: 1,
    membershipNumber: 'PENDING001',
    firstName: 'John',
    lastName: 'Smith',
    email: 'john.smith@example.com',
    membershipType: 'individual',
    status: 'pending',
    joinDate: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 2,
    membershipNumber: 'PENDING002',
    firstName: 'Mary',
    lastName: 'Johnson',
    email: 'mary.johnson@example.com',
    membershipType: 'family',
    status: 'pending',
    joinDate: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 3,
    membershipNumber: 'PENDING003',
    firstName: 'David',
    lastName: 'Williams',
    email: 'david.williams@example.com',
    membershipType: 'individual',
    status: 'pending',
    joinDate: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 4,
    membershipNumber: 'PENDING004',
    firstName: 'Sarah',
    lastName: 'Brown',
    email: 'sarah.brown@example.com',
    membershipType: 'youth',
    status: 'pending',
    joinDate: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 5,
    membershipNumber: 'PENDING005',
    firstName: 'Michael',
    lastName: 'Davis',
    email: 'michael.davis@example.com',
    membershipType: 'individual',
    status: 'pending',
    joinDate: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 6,
    membershipNumber: 'PENDING006',
    firstName: 'Emma',
    lastName: 'Wilson',
    email: 'emma.wilson@example.com',
    membershipType: 'family',
    status: 'pending',
    joinDate: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 7,
    membershipNumber: 'PENDING007',
    firstName: 'James',
    lastName: 'Taylor',
    email: 'james.taylor@example.com',
    membershipType: 'individual',
    status: 'pending',
    joinDate: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 8,
    membershipNumber: 'PENDING008',
    firstName: 'Sophie',
    lastName: 'Anderson',
    email: 'sophie.anderson@example.com',
    membershipType: 'youth',
    status: 'pending',
    joinDate: new Date(Date.now() - 9 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 9,
    membershipNumber: 'PENDING009',
    firstName: 'Oliver',
    lastName: 'Thomas',
    email: 'oliver.thomas@example.com',
    membershipType: 'individual',
    status: 'pending',
    joinDate: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 10,
    membershipNumber: 'PENDING010',
    firstName: 'Emily',
    lastName: 'Martin',
    email: 'emily.martin@example.com',
    membershipType: 'family',
    status: 'pending',
    joinDate: new Date(Date.now() - 11 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 11,
    membershipNumber: 'PENDING011',
    firstName: 'Daniel',
    lastName: 'White',
    email: 'daniel.white@example.com',
    membershipType: 'individual',
    status: 'pending',
    joinDate: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  {
    id: 12,
    membershipNumber: 'PENDING012',
    firstName: 'Lucy',
    lastName: 'Clark',
    email: 'lucy.clark@example.com',
    membershipType: 'youth',
    status: 'pending',
    joinDate: new Date(Date.now() - 13 * 60 * 60 * 1000).toISOString(),
    renewalDate: '',
  },
  // Members with upcoming renewals
  {
    id: 201,
    membershipNumber: 'HDL021',
    firstName: 'Peter',
    lastName: 'Morgan',
    email: 'peter.morgan@example.com',
    membershipType: 'individual',
    status: 'active',
    joinDate: '2023-01-01',
    renewalDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(), // 15 days from now
  },
  {
    id: 202,
    membershipNumber: 'HDL022',
    firstName: 'Laura',
    lastName: 'Bennett',
    email: 'laura.bennett@example.com',
    membershipType: 'family',
    status: 'active',
    joinDate: '2023-01-05',
    renewalDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(), // 20 days from now
  },
  {
    id: 203,
    membershipNumber: 'HDL023',
    firstName: 'Kevin',
    lastName: 'Hughes',
    email: 'kevin.hughes@example.com',
    membershipType: 'youth',
    status: 'active',
    joinDate: '2023-01-10',
    renewalDate: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString(), // 25 days from now
  },
];

export default MemberDetails;
